import { Autocomplete, Button, CircularProgress, IconButton, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import DiscussionModal from "components/Modals/DiscussionModal";
import { months, programLevel, states } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import { FaSearch, FaSlidersH, FaTimes } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import CourseGrid from "./CourseGrid";

const countryOptions = [
  { label: "Australia", value: "Australia" },
  { label: "New Zealand", value: "New Zealand" },
  { label: "UK", value: "UK" },
  { label: "USA", value: "USA" },
];

const FILTER_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "var(--app-surface)",
  },
};

const Course = ({ embedded = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ uniData }, { refetchUniData }] = useKothar();

  const [filters, setFilters] = useState({
    program: searchParams.get("program") || "",
    universityName: searchParams.get("universityName") || "",
    stateName: searchParams.get("stateName") || "",
    country: searchParams.get("country") || "",
    programLevel: [],
    intakes: [],
    year: searchParams.get("year") || "",
  });

  const [courseList, setCourseList] = useState([]);
  const [showResults, setShowResults] = useState(!!searchParams.get("program"));
  const [openDiscussion, setOpenDiscussion] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    if (!uniData?.length) refetchUniData();
  }, []);

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const buildSearchParams = (state) => {
    const params = new URLSearchParams();
    if (state.program) params.set("program", state.program);
    if (state.universityName) params.set("universityName", state.universityName);
    if (state.stateName) params.set("stateName", state.stateName);
    if (state.country) params.set("country", state.country);
    if (state.year) params.set("year", state.year);
    if (state.programLevel?.length)
      params.set("programLevel", state.programLevel.map((i) => i.value).join(","));
    if (state.intakes?.length) params.set("intakes", state.intakes.map((i) => i.value).join(","));
    return params;
  };

  const getCoursesData = async () => {
    const params = new URLSearchParams();
    if (filters.program) params.append("name", filters.program);
    if (filters.universityName) params.append("universityName", filters.universityName);
    if (filters.stateName) params.append("stateName", filters.stateName);
    if (filters.country) params.append("country", filters.country);
    if (filters.year) params.append("year", filters.year);
    if (filters.programLevel?.length)
      params.append("programLevel", filters.programLevel.map((i) => i.value).join(","));
    if (filters.intakes?.length)
      params.append("intakes", filters.intakes.map((i) => i.value).join(","));
    const response = await axios.get(`/university/course/query?${params.toString()}`);
    return response;
  };

  const { mutate: getCourses, isLoading } = useMutation(getCoursesData, {
    onSuccess(response) {
      setCourseList(response?.data?.data || []);
    },
  });

  useEffect(() => {
    if (searchParams.get("program")) {
      getCourses();
    }
  }, []);

  const runSearch = (e) => {
    e?.preventDefault?.();
    setShowResults(true);
    setSearchParams(buildSearchParams(filters));
    getCourses();
    setMobileFiltersOpen(false);
  };

  const clearAll = () => {
    setFilters({
      program: "",
      universityName: "",
      stateName: "",
      country: "",
      programLevel: [],
      intakes: [],
      year: "",
    });
    setShowResults(false);
    setCourseList([]);
    setSearchParams({});
  };

  const removeFilter = (key) => {
    const next = {
      ...filters,
      [key]: key === "programLevel" || key === "intakes" ? [] : "",
    };
    setFilters(next);
    if (showResults) {
      setSearchParams(buildSearchParams(next));
    }
  };

  const appliedChips = useMemo(() => {
    const chips = [];
    if (filters.program?.trim())
      chips.push({ key: "program", label: `Search: ${filters.program.trim()}` });
    if (filters.universityName)
      chips.push({ key: "universityName", label: `University: ${filters.universityName}` });
    if (filters.stateName) chips.push({ key: "stateName", label: `State: ${filters.stateName}` });
    if (filters.country)
      chips.push({
        key: "country",
        label: `Country: ${
          typeof filters.country === "object" ? filters.country?.label : filters.country
        }`,
      });
    if (filters.programLevel?.length)
      chips.push({
        key: "programLevel",
        label: `Level: ${filters.programLevel.map((i) => i.label).join(", ")}`,
      });
    if (filters.intakes?.length)
      chips.push({
        key: "intakes",
        label: `Intake: ${filters.intakes.map((i) => i.label).join(", ")}`,
      });
    if (filters.year) chips.push({ key: "year", label: `Year: ${filters.year}` });
    return chips;
  }, [filters]);

  const hasActiveFilters = appliedChips.length > 0;

  const FilterPanel = (
    <aside className="rounded-xl border border-app-border bg-app-surface p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaSlidersH className="text-app-text-muted" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-app-text">Filters</h2>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-medium text-sky-700 hover:underline"
          >
            Reset all
          </button>
        )}
      </div>

      <div className="space-y-5">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-app-text-secondary">
            University
          </label>
          <Autocomplete
            size="small"
            options={uniData || []}
            value={uniData?.find((i) => i.name === filters.universityName) || null}
            onChange={(_, v) => updateFilter("universityName", v?.name || "")}
            isOptionEqualToValue={(o, v) => o.name === v.name}
            getOptionLabel={(o) => o?.name || ""}
            sx={FILTER_FIELD_SX}
            renderInput={(p) => <TextField {...p} placeholder="Any university" />}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-app-text-secondary">
            State
          </label>
          <Autocomplete
            size="small"
            options={states || []}
            value={states?.find((i) => i.value === filters.stateName) || null}
            onChange={(_, v) => updateFilter("stateName", v?.value || "")}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            sx={FILTER_FIELD_SX}
            renderInput={(p) => <TextField {...p} placeholder="Any state" />}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-app-text-secondary">
            Country
          </label>
          <Autocomplete
            size="small"
            options={countryOptions}
            value={
              countryOptions.find(
                (i) =>
                  i.value === filters.country ||
                  (typeof filters.country === "object" && i.value === filters.country?.value)
              ) || null
            }
            onChange={(_, v) => updateFilter("country", v?.value || "")}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            sx={FILTER_FIELD_SX}
            renderInput={(p) => <TextField {...p} placeholder="Any country" />}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-app-text-secondary">
            Program level
          </label>
          <Autocomplete
            size="small"
            multiple
            options={programLevel || []}
            value={filters.programLevel || []}
            onChange={(_, v) => updateFilter("programLevel", v)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            sx={FILTER_FIELD_SX}
            renderInput={(p) => <TextField {...p} placeholder="e.g. Bachelor, Master" />}
            limitTags={2}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-app-text-secondary">
            Intake
          </label>
          <Autocomplete
            size="small"
            multiple
            options={months || []}
            value={filters.intakes || []}
            onChange={(_, v) => updateFilter("intakes", v)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            sx={FILTER_FIELD_SX}
            renderInput={(p) => <TextField {...p} placeholder="Pick months" />}
            limitTags={3}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-2">
        <Button variant="outlined" color="inherit" onClick={clearAll} disabled={!hasActiveFilters}>
          Clear
        </Button>
        <Button variant="contained" color="primary" onClick={runSearch}>
          Apply
        </Button>
      </div>
    </aside>
  );

  return (
    <div
      className={
        embedded
          ? "flex h-full min-h-[70vh] flex-col bg-transparent"
          : "flex h-full flex-col bg-app-surface-muted"
      }
    >
      <div className="border-b border-app-border bg-app-surface">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-5 sm:px-6">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-app-text">
                Course directory
              </h1>
              <p className="mt-1 text-sm text-app-text-muted">
                Search 20,000+ programs by name, university or location.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-md border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-text shadow-sm hover:bg-app-surface-muted lg:hidden"
            >
              <FaSlidersH /> Filters
              {hasActiveFilters && (
                <span className="rounded-full bg-sky-100 px-1.5 text-xs font-semibold text-sky-700">
                  {appliedChips.length}
                </span>
              )}
            </button>
          </div>

          <form onSubmit={runSearch} className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-text-muted" />
              <input
                type="text"
                value={filters.program}
                onChange={(e) => updateFilter("program", e.target.value)}
                placeholder="Search by program name (e.g. Bachelor of Nursing)"
                className="h-11 w-full rounded-lg border border-app-border bg-app-surface pl-10 pr-10 text-sm text-app-text outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              />
              {filters.program && (
                <IconButton
                  size="small"
                  onClick={() => updateFilter("program", "")}
                  sx={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}
                >
                  <FaTimes className="text-sm text-app-text-muted" />
                </IconButton>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ height: 44, minWidth: 120, borderRadius: "8px", textTransform: "none" }}
                startIcon={<FaSearch />}
              >
                Search
              </Button>
              {hasActiveFilters && (
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  onClick={clearAll}
                  sx={{ height: 44, borderRadius: "8px", textTransform: "none" }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          <div className={`${mobileFiltersOpen ? "block" : "hidden"} lg:block`}>{FilterPanel}</div>

          <section className="min-w-0">
            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-app-border bg-app-surface px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-app-text">
                  {!showResults
                    ? "Start searching"
                    : isLoading
                    ? "Searching…"
                    : `${courseList?.length || 0} ${
                        (courseList?.length || 0) === 1 ? "result" : "results"
                      }`}
                </p>
                <p className="mt-0.5 text-xs text-app-text-muted">
                  {!showResults
                    ? "Use the search bar or filters to find a program."
                    : filters.program?.trim()
                    ? `Matching “${filters.program.trim()}”`
                    : "Showing programs that match your filters."}
                </p>
              </div>
              {showResults && hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="self-start text-sm font-medium text-sky-700 hover:underline"
                >
                  Clear all filters
                </button>
              )}
            </div>

            {showResults && hasActiveFilters && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-app-text-muted">
                  Filters:
                </span>
                {appliedChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1.5 rounded-full border border-app-border bg-app-surface px-2.5 py-1 text-xs font-medium text-app-text shadow-sm"
                  >
                    {chip.label}
                    <button
                      type="button"
                      onClick={() => removeFilter(chip.key)}
                      className="rounded-full p-0.5 text-app-text-muted hover:bg-app-surface-muted hover:text-app-text"
                      aria-label={`Remove ${chip.label}`}
                    >
                      <FaTimes className="text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {isLoading ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-app-border bg-app-surface">
                <CircularProgress />
                <p className="text-sm text-app-text-muted">Looking up programs…</p>
              </div>
            ) : !showResults ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed border-app-border bg-app-surface px-6 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-app-surface-muted text-app-text-muted">
                  <FaSearch />
                </div>
                <p className="text-base font-semibold text-app-text">Find a program</p>
                <p className="mt-1 max-w-md text-sm text-app-text-muted">
                  Type a program name above or pick a filter on the left, then hit{" "}
                  <strong>Search</strong>.
                </p>
              </div>
            ) : courseList?.length > 0 ? (
              <CourseGrid courses={courseList} />
            ) : (
              <div className="flex min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed border-app-border bg-app-surface px-6 text-center">
                <p className="text-base font-semibold text-app-text">No matching programs</p>
                <p className="mt-1 max-w-md text-sm text-app-text-muted">
                  Try a different keyword or remove some filters.
                </p>
                {hasActiveFilters && (
                  <Button onClick={clearAll} sx={{ mt: 2, textTransform: "none" }}>
                    Reset filters
                  </Button>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* {openDiscussion && <DiscussionModal open={openDiscussion} setOpen={setOpenDiscussion} />} */}
    </div>
  );
};

export default Course;
